import React from 'react'
import { Box, Divider, Typography } from '@mui/material'

import { INTERAX_COLORS } from '../../constants/colors'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { SettingsFieldType, type SettingsSectionSchema, type SettingsFieldValue, type SettingsValues } from './types'
import { SettingToggleRow } from './fields/SettingToggleRow'

export interface SettingsSectionProps {
  schema: SettingsSectionSchema
  values: SettingsValues
  onChange: (key: string, value: SettingsFieldValue) => void
  disabled?: boolean
  footer?: React.ReactNode
}

// Themed card that renders a settings schema as rows.
export const SettingsSection: React.FC<SettingsSectionProps> = ({ schema, values, onChange, disabled, footer }) => {
  const { t } = useTranslation()
  const visibleFields = schema.fields.filter((field) => !field.hidden)

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 3 },
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '12px',
        border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
        mb: 3
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontSize: { xs: '18px', sm: '20px', md: '20px' }, fontWeight: 600, color: INTERAX_COLORS.white }}
      >
        {t(schema.titleKey)}
      </Typography>
      {schema.descriptionKey && (
        <Typography sx={{ mt: 1, fontSize: '14px', color: INTERAX_COLORS.thunderGrey }}>
          {t(schema.descriptionKey)}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        {visibleFields.map((field, index) => (
          <Box key={field.key}>
            {index > 0 && <Divider sx={{ borderColor: INTERAX_COLORS.white10 }} />}
            {field.type === SettingsFieldType.TOGGLE && (
              <SettingToggleRow
                field={field}
                value={values[field.key] === true}
                onChange={(v) => onChange(field.key, v)}
                disabled={disabled}
              />
            )}
          </Box>
        ))}
      </Box>

      {footer && <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>{footer}</Box>}
    </Box>
  )
}
